import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@atoms/Modal';
import { Spinner } from '@atoms/Spinner';
import { ProductCard } from '@molecules/ProductCard';
import { searchService } from '@services/searchService';
import { useAuthStore } from '@store/authStore';
import { useCartStore } from '@store/cartStore';
import type { Category, Product } from '@/types';

interface CategoryPreviewModalProps {
  category: Category | null;
  onClose: () => void;
}

export function CategoryPreviewModal({ category, onClose }: CategoryPreviewModalProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const addItem = useCartStore((s) => s.addItem);

  const { data: products, isLoading } = useQuery({
    queryKey: ['search', 'category', category?.name],
    queryFn: () => searchService.searchProducts(category!.name),
    enabled: Boolean(category?.name),
  });

  const handleAdd = async (product: Product) => {
    if (!isAuthenticated || user?.role !== 'restaurant') {
      navigate(`/login?redirect=${encodeURIComponent('/restaurant?tab=browse')}`);
      return;
    }
    await addItem(product.id);
  };

  const seeAll = () => {
    if (!category) return;
    if (isAuthenticated && user?.role === 'restaurant') {
      navigate(`/restaurant?tab=browse&category=${encodeURIComponent(category.name)}`);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(`/restaurant?tab=browse&category=${encodeURIComponent(category.name)}`)}`);
    }
    onClose();
  };

  return (
    <Modal open={Boolean(category)} onClose={onClose} title={category?.name ?? ''}>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {(products ?? []).slice(0, 6).map((product) => (
              <ProductCard key={product.id} product={product} onAdd={handleAdd} showWholesaler />
            ))}
          </div>
          {!products?.length && (
            <p className="text-center text-sm text-gray-500">Bu kategoride ürün bulunamadı.</p>
          )}
          <button
            type="button"
            onClick={seeAll}
            className="w-full rounded-lg bg-brand-500 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            Tümünü Gör
          </button>
        </div>
      )}
    </Modal>
  );
}
